// Shapes the workspace activity feed for display:
//  - consecutive events of the same kind, by the same person, for the same
//    team, on the same day collapse into one row ("added 4 athletes…")
//  - rows are bucketed under day headings (today, yesterday, then dates)

// Activity types that read naturally as a count ("added 4 athletes").
export const GROUPABLE_TYPES = [
  "player_created",
  "player_added",
  "session_created",
  "session_uploaded",
];

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Local calendar day as a sortable key, e.g. "2026-09-25".
export const dayKey = (date) => {
  const d = startOfDay(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// "today" | "yesterday" | "earlier" relative to `now`.
export const dayKind = (date, now = new Date()) => {
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return "earlier";
};

const canMerge = (a, b) =>
  GROUPABLE_TYPES.includes(a.activity_type) &&
  a.activity_type === b.activity_type &&
  (a.actor_name ?? "") === (b.actor_name ?? "") &&
  (a.team_id ?? null) === (b.team_id ?? null) &&
  dayKey(new Date(a.created_at)) === dayKey(new Date(b.created_at));

/**
 * @param {Array} activities - newest first, as returned by the API
 * @param {Date} now
 * @param {number} maxRows - cap on rows shown after grouping
 * @returns {Array<{ key, kind, date, rows: Array<{ activity, members }> }>}
 *   `activity` is the newest event in the row; `members` is every event it
 *   stands for (length 1 for an ungrouped row).
 */
export const groupActivities = (activities, now = new Date(), maxRows = 10) => {
  const rows = [];
  for (const activity of activities) {
    const previous = rows[rows.length - 1];
    if (previous && canMerge(previous.activity, activity)) {
      previous.members.push(activity);
    } else {
      rows.push({ activity, members: [activity] });
    }
  }

  const days = [];
  for (const row of rows.slice(0, maxRows)) {
    const date = new Date(row.activity.created_at);
    const key = dayKey(date);
    let day = days[days.length - 1];
    if (!day || day.key !== key) {
      day = { key, kind: dayKind(date, now), date, rows: [] };
      days.push(day);
    }
    day.rows.push(row);
  }
  return days;
};
